// UI store — Zustand.
//
// Persists theme/density/accent to localStorage; persists currentMerchantId
// to localStorage too, so a new tab / window / restart lands on the merchant
// the user was last working in. (It used to be sessionStorage, which is
// per-tab — every new tab silently fell back to the first membership.)
//
// Toast state is ephemeral (never persisted); toasts live here so any component
// can fire a toast and the App shell renders them via <ToastStack>.
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type Theme = "light" | "dark";
export type Density = "compact" | "regular" | "comfortable";

export interface ToastEntry {
  id: string;
  title: string;
  body?: string;
  /** Variant drives screen-reader announcement urgency in <ToastStack>.
   *  "success" (default) → role="status", aria-live="polite".
   *  "error" → role="alert", aria-live="assertive" (interrupts current speech).
   *  AUDIT-PASS-6 UI CRITICAL #3: this field was added to ToastItem (UI) but
   *  forgotten on ToastEntry (store), so every addToast() call lost the
   *  variant — screen readers got polite role="status" for every toast
   *  regardless of intent. Now the field lives end-to-end. */
  variant?: "success" | "error";
}

interface UIState {
  theme: Theme;
  density: Density;
  accent: string;

  /** Selected merchant id (int8 from the backend) — kept for any caller that
   *  still wants the numeric form (e.g. comparing membership.merchant_id to
   *  the active selection). The wire/URL identifier going forward is the slug
   *  below; the backend accepts either form. */
  currentMerchantId: number | null;
  /** Selected merchant slug (from /v1/me memberships). Recommended for URL
   *  construction and X-Merchant-ID. Null when no merchant is selected or
   *  when the source membership row carried no slug. */
  currentMerchantSlug: string | null;

  /** Ephemeral toast queue — rendered by <ToastStack> in App. Not persisted. */
  toasts: ToastEntry[];

  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  setAccent: (hex: string) => void;
  /** Set the current merchant. Both id and slug move together — pass slug=null
   *  only if the caller genuinely has no slug for this merchant (which
   *  shouldn't happen in normal flows; /v1/me always includes slug). */
  setCurrentMerchant: (id: number | null, slug: string | null) => void;
  /** Deprecated convenience for callers that only have the id. Sets slug=null. */
  setCurrentMerchantId: (id: number | null) => void;
  /** Push a toast onto the queue; auto-removes it after 4 seconds. */
  addToast: (toast: Omit<ToastEntry, "id">) => void;
  removeToast: (id: string) => void;
}

// Theme / density / accent — long-lived prefs.
export const useUI = create<UIState>()(
  persist(
    (set) => ({
      theme: "light",
      density: "regular",
      accent: "#2A6FDB",
      currentMerchantId: null,
      currentMerchantSlug: null,
      toasts: [],
      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      setAccent: (accent) => set({ accent }),
      setCurrentMerchant: (currentMerchantId, currentMerchantSlug) =>
        set({ currentMerchantId, currentMerchantSlug }),
      setCurrentMerchantId: (currentMerchantId) =>
        set({ currentMerchantId, currentMerchantSlug: null }),
      addToast: (toast) => {
        const id = Math.random().toString(36).slice(2);
        set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }));
        setTimeout(() => {
          set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
        }, 4000);
      },
      removeToast: (id) =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    }),
    {
      name: "strata.ui",
      storage: createJSONStorage(() => localStorage),
      // currentMerchantId and toasts live outside persisted state.
      partialize: (s) => ({
        theme: s.theme,
        density: s.density,
        accent: s.accent,
      }),
    },
  ),
);

// Read currentMerchantId + currentMerchantSlug from localStorage at boot.
// Two keys so an older session with only the int still hydrates correctly;
// once /v1/me lands the slug pair will be set together. The merchant id/slug
// is not a secret — it's the same value that goes out in X-Merchant-ID on
// every request — so localStorage is fine here (tokens stay in memory).
//
// Falls back to the legacy sessionStorage keys once so a tab that was open
// across the upgrade keeps its merchant instead of snapping to the default.
const MERCHANT_KEY = "strata.currentMerchantId";
const MERCHANT_SLUG_KEY = "strata.currentMerchantSlug";

if (typeof window !== "undefined") {
  try {
    const raw =
      localStorage.getItem(MERCHANT_KEY) ??
      sessionStorage.getItem(MERCHANT_KEY);
    const slugRaw =
      localStorage.getItem(MERCHANT_SLUG_KEY) ??
      sessionStorage.getItem(MERCHANT_SLUG_KEY);
    if (raw) {
      const id = parseInt(raw, 10);
      if (!Number.isNaN(id)) {
        useUI.setState({
          currentMerchantId: id,
          currentMerchantSlug: slugRaw && slugRaw.length > 0 ? slugRaw : null,
        });
      }
    }
  } catch {
    // ignore — storage can be unavailable (private mode, embed contexts)
  }
  // Mirror future changes back into localStorage.
  useUI.subscribe((state) => {
    try {
      if (state.currentMerchantId == null) {
        localStorage.removeItem(MERCHANT_KEY);
        localStorage.removeItem(MERCHANT_SLUG_KEY);
      } else {
        localStorage.setItem(MERCHANT_KEY, String(state.currentMerchantId));
        if (state.currentMerchantSlug) {
          localStorage.setItem(MERCHANT_SLUG_KEY, state.currentMerchantSlug);
        } else {
          localStorage.removeItem(MERCHANT_SLUG_KEY);
        }
      }
    } catch {
      // ignore
    }
  });
}
